'use client';

/**
 * NodeRenderer - Recursive template node interpreter
 * 
 * This component is the core of the dynamic rendering engine.
 * It interprets TemplateNode structures and renders them as React elements.
 * 
 * Features:
 * - Recursive rendering of nested children
 * - Conditional rendering (if/then/else)
 * - Array iteration (map)
 * - Data binding resolution
 * - Special component rendering
 * - Style tag injection
 */

import React, { Suspense } from 'react';
import { TemplateNode, TemplateContext, TemplateNodeChildren } from '@/types/template-schema';
import { 
  resolveValue, 
  resolveProps, 
  resolveStyles, 
  evaluateCondition,
  resolveArray,
  createMapItemContext,
  resolveChildContent,
} from './DataResolver';
import { ComponentRegistry, isVoidElement } from './ComponentRegistry';

// ============================================================================
// Types
// ============================================================================

export interface NodeRendererProps {
  /** The template node to render */
  node: TemplateNode | TemplateNode[] | string | number | null | undefined;
  /** The rendering context with data bindings */
  context: TemplateContext;
  /** Optional key for React reconciliation */
  nodeKey?: string | number;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Generate a unique key for a node
 */
function generateNodeKey(node: TemplateNode, index: number, parentKey?: string | number): string {
  const baseKey = node.key || node.id || node.type || 'node';
  return parentKey !== undefined ? `${parentKey}-${baseKey}-${index}` : `${baseKey}-${index}`;
}

/**
 * Render children content
 */
function renderChildren(
  children: TemplateNodeChildren | undefined,
  context: TemplateContext,
  parentKey?: string | number
): React.ReactNode {
  if (children === undefined || children === null) {
    return null;
  }

  // Handle string children (may be data bindings)
  if (typeof children === 'string') {
    const resolved = resolveChildContent(children, context);
    if (typeof resolved === 'string' || typeof resolved === 'number') {
      return resolved;
    }
    // Handle responsive image objects - extract URL instead of stringifying
    if (resolved !== undefined && resolved !== null && typeof resolved === 'object' && !Array.isArray(resolved)) {
      // Check if it's a responsive image object
      if ('desktop' in resolved || 'mobile' in resolved) {
        const imgObj = resolved as { desktop?: string; mobile?: string };
        // Return desktop URL if available, otherwise mobile, otherwise empty string
        return imgObj.desktop || imgObj.mobile || '';
      }
      // For other objects, try to extract a meaningful string value
      if ('url' in resolved && typeof (resolved as { url: unknown }).url === 'string') {
        return (resolved as { url: string }).url;
      }
      if ('src' in resolved && typeof (resolved as { src: unknown }).src === 'string') {
        return (resolved as { src: string }).src;
      }
      // Last resort: stringify (but this should be avoided)
      console.warn('Converting object to string in text content:', resolved);
      return String(resolved);
    }
    return null;
  }

  // Handle number children
  if (typeof children === 'number') {
    return children;
  }

  // Handle single node
  if (!Array.isArray(children) && typeof children === 'object') {
    return <NodeRenderer node={children} context={context} nodeKey={parentKey} />;
  }

  // Handle array of children
  if (Array.isArray(children)) {
    return children.map((child, index) => {
      if (typeof child === 'string') {
        const resolved = resolveChildContent(child, context);
        // Ensure resolved value is a valid ReactNode
        let nodeValue: string | number;
        if (typeof resolved === 'string' || typeof resolved === 'number') {
          nodeValue = resolved;
        } else if (resolved !== undefined && resolved !== null && typeof resolved === 'object' && !Array.isArray(resolved)) {
          // Handle responsive image objects - extract URL instead of stringifying
          if ('desktop' in resolved || 'mobile' in resolved) {
            const imgObj = resolved as { desktop?: string; mobile?: string };
            nodeValue = imgObj.desktop || imgObj.mobile || '';
          } else if ('url' in resolved && typeof (resolved as { url: unknown }).url === 'string') {
            nodeValue = (resolved as { url: string }).url;
          } else if ('src' in resolved && typeof (resolved as { src: unknown }).src === 'string') {
            nodeValue = (resolved as { src: string }).src;
          } else {
            console.warn('Converting object to string in text content:', resolved);
            nodeValue = String(resolved ?? '');
          }
        } else {
          nodeValue = String(resolved ?? '');
        }
        return <React.Fragment key={`text-${index}`}>{nodeValue}</React.Fragment>;
      }
      if (typeof child === 'number') {
        return <React.Fragment key={`num-${index}`}>{child}</React.Fragment>;
      }
      if (child && typeof child === 'object') {
        return (
          <NodeRenderer
            key={generateNodeKey(child as TemplateNode, index, parentKey)}
            node={child}
            context={context}
            nodeKey={`${parentKey || 'root'}-${index}`}
          />
        );
      }
      return null;
    });
  }

  return null;
}

// ============================================================================
// Conditional Renderer
// ============================================================================

interface ConditionalRendererProps {
  node: TemplateNode;
  context: TemplateContext;
  nodeKey?: string | number;
}

function ConditionalRenderer({ node, context, nodeKey }: ConditionalRendererProps): React.ReactElement | null {
  if (!node.conditional) return null;

  const { if: condition, then: thenContent, else: elseContent } = node.conditional;
  const conditionMet = evaluateCondition(condition, context);

  if (conditionMet) {
    if (thenContent === undefined || thenContent === null) return null;
    return <NodeRenderer node={thenContent as TemplateNode} context={context} nodeKey={`${nodeKey}-then`} />;
  }

  if (elseContent !== undefined && elseContent !== null) {
    return <NodeRenderer node={elseContent as TemplateNode} context={context} nodeKey={`${nodeKey}-else`} />;
  }

  return null;
}

// ============================================================================
// Map Renderer
// ============================================================================

interface MapRendererProps {
  node: TemplateNode;
  context: TemplateContext;
  nodeKey?: string | number;
}

function MapRenderer({ node, context, nodeKey }: MapRendererProps): React.ReactElement | null {
  if (!node.map) return null;

  const { from, as, template } = node.map;
  const items = resolveArray(from, context);

  if (!items || items.length === 0) return null;

  return (
    <>
      {items.map((item, index) => {
        const itemContext = createMapItemContext(context, item, as, index);
        return (
          <NodeRenderer
            key={`${nodeKey}-map-${index}`}
            node={template as TemplateNode}
            context={itemContext}
            nodeKey={`${nodeKey}-map-${index}`}
          />
        );
      })}
    </>
  );
}

// ============================================================================
// Style Tag Renderer
// ============================================================================

interface StyleTagRendererProps {
  styleTag: string;
}

function StyleTagRenderer({ styleTag }: StyleTagRendererProps): React.ReactElement {
  return <style dangerouslySetInnerHTML={{ __html: styleTag }} />;
}

// ============================================================================
// HTML Element Renderer
// ============================================================================

interface HTMLElementRendererProps {
  node: TemplateNode;
  context: TemplateContext;
  nodeKey?: string | number;
}

function HTMLElementRenderer({ node, context, nodeKey }: HTMLElementRendererProps): React.ReactElement | null {
  const { type, props, style, className, children, id, conditional, map, styleTag } = node;

  // Resolve props and styles
  const resolvedProps = resolveProps(props as Record<string, unknown>, context) || {};
  const resolvedStyles = resolveStyles(style, context);

  // Build element props
  const elementProps: Record<string, unknown> = {
    ...resolvedProps,
    style: resolvedStyles,
    className,
    id,
  };

  // Remove undefined props
  Object.keys(elementProps).forEach(key => {
    if (elementProps[key] === undefined) {
      delete elementProps[key];
    }
  });

  // Extract data-field attribute from sectionData bindings in children
  // This enables click-to-edit functionality in the page builder
  if (typeof children === 'string') {
    // Match {{sectionData.fieldName}} or sectionData.fieldName bindings
    const bindingMatch = children.match(/\{\{sectionData\.([^}|]+)(?:\s*\|\|[^}]*)?\}\}|^sectionData\.([a-zA-Z0-9_]+)$/);
    if (bindingMatch) {
      elementProps['data-field'] = bindingMatch[1] || bindingMatch[2];
    }
  }

  // Handle special cases for certain elements
  if (type === 'a' && resolvedProps.href) {
    elementProps.href = resolveValue(resolvedProps.href, context);
  }

  if (type === 'img' && resolvedProps.src) {
    const srcValue = resolveValue(resolvedProps.src, context);
    // Handle responsive image objects { mobile?: string, desktop?: string }
    if (typeof srcValue === 'object' && srcValue !== null && !Array.isArray(srcValue)) {
      // Use desktop if available, otherwise mobile, otherwise empty string
      elementProps.src = (srcValue as { desktop?: string; mobile?: string }).desktop || 
                        (srcValue as { desktop?: string; mobile?: string }).mobile || 
                        '';
    } else if (typeof srcValue === 'string') {
      elementProps.src = srcValue;
    } else {
      // Convert to string if it's not already
      elementProps.src = String(srcValue || '');
    }
    
    const altValue = resolveValue(resolvedProps.alt || '', context);
    elementProps.alt = typeof altValue === 'string' ? altValue : String(altValue || '');
  }

  // Render style tag specially
  if (type === 'style' && styleTag) {
    return <StyleTagRenderer styleTag={styleTag} />;
  }

  // Handle void elements (self-closing)
  if (isVoidElement(type)) {
    return React.createElement(type, elementProps);
  }

  // Determine content to render
  let content: React.ReactNode = null;

  // Check for conditional rendering inside the element
  if (conditional) {
    content = <ConditionalRenderer node={node} context={context} nodeKey={nodeKey} />;
  }
  // Check for map rendering inside the element
  else if (map) {
    content = <MapRenderer node={node} context={context} nodeKey={nodeKey} />;
  }
  // Regular children
  else {
    content = renderChildren(children, context, nodeKey);
  }

  // Check if this is a registered component (shouldn't happen, but safety check)
  if (ComponentRegistry.isSpecialComponent(type)) {
    console.warn(`HTMLElementRenderer received registered component "${type}", redirecting to SpecialComponentRenderer`);
    return <SpecialComponentRenderer node={node} context={context} nodeKey={nodeKey} />;
  }

  // Check if this is a <p> tag that contains block-level components (like MarkdownRenderer)
  // If so, convert it to a <div> to avoid HTML validation errors
  const actualType = (type === 'p' && hasBlockLevelChildren(node)) ? 'div' : type;

  // Validate that actualType is a valid HTML element (lowercase)
  // React.createElement requires lowercase for HTML elements
  if (actualType && typeof actualType === 'string' && !ComponentRegistry.isHTMLElement(actualType)) {
    console.error(`Invalid HTML element type: "${actualType}". Use a registered component or valid HTML element.`);
    return null;
  }

  // Inject styleTag as first child if present (and not a style element)
  if (styleTag && type !== 'style') {
    content = (
      <>
        <StyleTagRenderer styleTag={styleTag} />
        {content}
      </>
    );
  }

  return React.createElement(actualType, elementProps, content);
}

/**
 * Check if a node contains block-level components that shouldn't be inside a <p> tag
 */
function hasBlockLevelChildren(node: TemplateNode): boolean {
  // Check conditional blocks
  if (node.conditional) {
    const { then: thenContent, else: elseContent } = node.conditional;
    if (thenContent && checkContentForBlockLevel(thenContent)) return true;
    if (elseContent && checkContentForBlockLevel(elseContent)) return true;
  }
  
  // Check map blocks
  if (node.map?.template) {
    if (checkContentForBlockLevel(node.map.template)) return true;
  }
  
  // Check children
  if (!node.children) return false;
  
  return checkContentForBlockLevel(node.children);
}

/**
 * Helper to check if content contains block-level components
 */
function checkContentForBlockLevel(content: unknown): boolean {
  if (!content) return false;
  
  if (typeof content === 'string' || typeof content === 'number') return false;
  
  const contentArray = Array.isArray(content) ? content : [content];
  
  return contentArray.some(child => {
    if (typeof child === 'string' || typeof child === 'number') return false;
    
    const childNode = child as TemplateNode;
    // MarkdownRenderer and other block components
    if (childNode.type === 'Markdown' || childNode.type === 'MarkdownRenderer') {
      return true;
    }
    
    // Recursively check the child node
    if (hasBlockLevelChildren(childNode)) {
      return true;
    }
    
    return false;
  });
}

// ============================================================================
// Special Component Renderer
// ============================================================================

interface SpecialComponentRendererProps {
  node: TemplateNode;
  context: TemplateContext;
  nodeKey?: string | number;
}

function SpecialComponentRenderer({ node, context, nodeKey }: SpecialComponentRendererProps): React.ReactElement | null {
  const { type, props, style, className, children, conditional, map, styleTag } = node;

  // Get the component from registry
  const entry = ComponentRegistry.get(type);
  if (!entry) {
    console.warn(`Unknown special component type: ${type}`);
    return null;
  }

  const Component = entry.component;

  // Resolve props and styles
  const resolvedProps = resolveProps(props as Record<string, unknown>, context) || {};
  const resolvedStyles = resolveStyles(style, context);

  // Merge with default props
  const mergedProps: Record<string, unknown> = {
    ...entry.defaultProps,
    ...resolvedProps,
    style: resolvedStyles,
    className,
  };

  // Remove undefined props
  Object.keys(mergedProps).forEach(key => {
    if (mergedProps[key] === undefined) {
      delete mergedProps[key];
    }
  });

  // Handle conditional and map at the content level
  let content: React.ReactNode = null;

  if (conditional) {
    content = <ConditionalRenderer node={node} context={context} nodeKey={nodeKey} />;
  } else if (map) {
    content = <MapRenderer node={node} context={context} nodeKey={nodeKey} />;
  } else {
    content = renderChildren(children, context, nodeKey);
  }

  // Some components (like Markdown) take content as a prop
  if (type === 'Markdown' || type === 'MarkdownRenderer') {
    if (mergedProps.content) {
      mergedProps.content = resolveValue(mergedProps.content, context);
    }
  }

  // Handle S3FilePreview src prop - preserve responsive objects for device-aware selection
  // Also extract field binding for live editing support
  if (type === 'S3FilePreview' && mergedProps.src) {
    const srcValue = mergedProps.src;
    // Handle responsive image objects { mobile?: string, desktop?: string }
    // S3FilePreview accepts ResponsiveBackgroundImage, so preserve the object
    // It will use useResponsiveVideo hook to select the correct URL based on device
    if (typeof srcValue === 'object' && srcValue !== null && !Array.isArray(srcValue)) {
      // Check if it's a responsive object with mobile/desktop properties
      if ('desktop' in srcValue || 'mobile' in srcValue) {
        // Preserve the object - S3FilePreview will handle device detection
        mergedProps.src = srcValue as { desktop?: string; mobile?: string };
      } else {
        // Not a responsive object, convert to string
        mergedProps.src = String(srcValue || '');
      }
    } else if (typeof srcValue !== 'string') {
      // Convert to string if it's not already
      mergedProps.src = String(srcValue || '');
    }
    
    // Extract field binding from original props for live editor data-field attribute
    // Look for sectionData bindings like {{sectionData.backgroundImage}} or sectionData.image
    const originalSrcProp = (props as Record<string, unknown> | undefined)?.src;
    if (typeof originalSrcProp === 'string') {
      const bindingMatch = originalSrcProp.match(/\{\{sectionData\.([^}]+)\}\}|^sectionData\.(.+)$/);
      if (bindingMatch) {
        mergedProps['data-field'] = bindingMatch[1] || bindingMatch[2];
      }
    }
  }

  // Handle BackgroundVideo videoUrl prop - ensure it's always a string or responsive object
  // Also extract field binding for live editing support
  if (type === 'BackgroundVideo' && mergedProps.videoUrl) {
    const videoUrlValue = mergedProps.videoUrl;
    // Handle responsive video objects { mobile?: string, desktop?: string }
    if (typeof videoUrlValue === 'object' && videoUrlValue !== null && !Array.isArray(videoUrlValue)) {
      // BackgroundVideo component handles responsive objects internally, so just pass through
      // But extract desktop URL for data-field detection on the container
      const desktopUrl = (videoUrlValue as { desktop?: string; mobile?: string }).desktop || 
                         (videoUrlValue as { desktop?: string; mobile?: string }).mobile || 
                         '';
      if (!desktopUrl) {
        mergedProps.videoUrl = '';
      }
    } else if (typeof videoUrlValue !== 'string') {
      // Convert to string if it's not already
      mergedProps.videoUrl = String(videoUrlValue || '');
    }
    
    // Extract field binding from original props for live editor data-field attribute
    // Look for sectionData bindings like {{sectionData.backgroundVideo}} or sectionData.videoUrl
    const originalVideoUrlProp = (props as Record<string, unknown> | undefined)?.videoUrl;
    if (typeof originalVideoUrlProp === 'string') {
      const bindingMatch = originalVideoUrlProp.match(/\{\{sectionData\.([^}]+)\}\}|^sectionData\.(.+)$/);
      if (bindingMatch) {
        mergedProps['data-field'] = bindingMatch[1] || bindingMatch[2];
      }
    }
  }

  // Handle ImageCircle src and alt props - ensure they're always strings
  if (type === 'ImageCircle') {
    if (mergedProps.src) {
      const srcValue = resolveValue(mergedProps.src, context);
      // Handle responsive image objects { mobile?: string, desktop?: string }
      if (typeof srcValue === 'object' && srcValue !== null && !Array.isArray(srcValue)) {
        // Use desktop if available, otherwise mobile, otherwise default image
        mergedProps.src = (srcValue as { desktop?: string; mobile?: string }).desktop || 
                          (srcValue as { desktop?: string; mobile?: string }).mobile || 
                          'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=400&fit=crop';
      } else if (typeof srcValue !== 'string') {
        // Convert to string if it's not already, use default if empty
        mergedProps.src = String(srcValue || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=400&fit=crop');
      } else {
        mergedProps.src = srcValue || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=400&fit=crop';
      }
    } else {
      // Default image if no src provided
      mergedProps.src = 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=400&fit=crop';
    }
    if (mergedProps.alt) {
      const altValue = resolveValue(mergedProps.alt, context);
      mergedProps.alt = typeof altValue === 'string' ? altValue : String(altValue || 'Training');
    } else {
      mergedProps.alt = 'Training';
    }
    // Ensure zIndex is a number
    if (mergedProps.zIndex !== undefined) {
      const zIndexValue = resolveValue(mergedProps.zIndex, context);
      mergedProps.zIndex = typeof zIndexValue === 'number' ? zIndexValue : Number(zIndexValue || 20);
    }
    // Ensure overflow is boolean
    if (mergedProps.overflow !== undefined) {
      mergedProps.overflow = Boolean(mergedProps.overflow);
    }
  }

  // Handle ColorCircle props - resolve color and ensure zIndex is a number
  if (type === 'ColorCircle') {
    if (mergedProps.color) {
      const colorValue = resolveValue(mergedProps.color, context);
      mergedProps.color = typeof colorValue === 'string' ? colorValue : String(colorValue || '#59A047');
    }
    if (mergedProps.zIndex !== undefined) {
      const zIndexValue = resolveValue(mergedProps.zIndex, context);
      mergedProps.zIndex = typeof zIndexValue === 'number' ? zIndexValue : Number(zIndexValue || 0);
    }
    // Ensure pointerEvents is boolean
    if (mergedProps.pointerEvents !== undefined) {
      mergedProps.pointerEvents = Boolean(mergedProps.pointerEvents);
    }
  }

  // Handle LucideIcon name prop - resolve the icon name or use index
  if (type === 'LucideIcon') {
    if (mergedProps.name) {
      const nameValue = resolveValue(mergedProps.name, context);
      if (nameValue !== undefined) {
        mergedProps.name = nameValue;
      }
    }
    // If name is still not set or is 'currentIndex', use currentIndex from context
    if (!mergedProps.name || mergedProps.name === 'currentIndex') {
      const index = context.currentIndex;
      if (typeof index === 'number') {
        mergedProps.name = index; // Pass index directly, component will map it
      } else {
        mergedProps.name = 0; // default to first icon
      }
    }
  }

  // Handle AccordionItem value prop - ensure it's always a unique string
  if (type === 'AccordionItem') {
    // Always use nodeKey as the primary source for uniqueness since it's guaranteed to be unique
    // This ensures each AccordionItem gets a distinct value even if template resolution fails
    const baseUniqueId = nodeKey ? String(nodeKey).replace(/[^a-zA-Z0-9-]/g, '-') : 
                          (context.currentIndex !== undefined ? `item-${context.currentIndex}` : 
                          `item-${Math.random().toString(36).substring(7)}`);
    
    if (mergedProps.value !== undefined) {
      const rawValue = mergedProps.value;
      // Normalize to string for safe string operations
      let value = typeof rawValue === 'string' ? rawValue : String(rawValue ?? '');

      // If value contains template string that wasn't resolved, try to resolve it
      if (value.includes('{{currentIndex}}')) {
        const index = context.currentIndex;
        value = value.replace(/\{\{currentIndex\}\}/g, String(index ?? 0));
      }
      // Also check if the value still contains unresolved bindings
      if (value.includes('{{')) {
        // Try to resolve any remaining bindings
        const pattern = /\{\{([^}]+)\}\}/g;
        value = value.replace(pattern, (match, binding) => {
          const resolved = resolveValue(binding.trim(), context);
          return resolved !== undefined && resolved !== null ? String(resolved) : match;
        });
      }

      // Final check: if value is still not unique, empty, or contains unresolved bindings, use baseUniqueId
      if (!value || 
          value === 'undefined' || 
          value === 'null' ||
          value.includes('{{')) {
        value = `accordion-${baseUniqueId}`;
      }

      mergedProps.value = value;
    } else {
      // If no value provided, generate a unique one using baseUniqueId
      mergedProps.value = `accordion-${baseUniqueId}`;
    }
  }

  // Determine fallback based on context - use span for inline contexts
  // For now, use a simple text fallback to avoid HTML validation issues
  const fallback = <span className="text-zinc-400">Loading...</span>;

  // Wrap in Suspense for lazy-loaded components
  const element = (
    <Suspense fallback={fallback}>
      <Component {...mergedProps}>
        {content}
      </Component>
    </Suspense>
  );

  // Inject styleTag if present
  if (styleTag) {
    return (
      <>
        <StyleTagRenderer styleTag={styleTag} />
        {element}
      </>
    );
  }

  return element;
}

// ============================================================================
// Main NodeRenderer Component
// ============================================================================

export function NodeRenderer({ node, context, nodeKey }: NodeRendererProps): React.ReactElement | null {
  // Handle null/undefined
  if (node === null || node === undefined) {
    return null;
  }

  // Handle string content
  if (typeof node === 'string') {
    const resolved = resolveChildContent(node, context);
    return <>{resolved}</>;
  }

  // Handle number content
  if (typeof node === 'number') {
    return <>{node}</>;
  }

  // Handle array of nodes
  if (Array.isArray(node)) {
    return (
      <>
        {node.map((child, index) => (
          <NodeRenderer
            key={`${nodeKey || 'array'}-${index}`}
            node={child}
            context={context}
            nodeKey={`${nodeKey || 'array'}-${index}`}
          />
        ))}
      </>
    );
  }

  // Handle TemplateNode
  const templateNode = node as TemplateNode;
  const { type } = templateNode;

  // Determine renderer type
  const rendererType = ComponentRegistry.getRendererType(type);

  switch (rendererType) {
    case 'html':
      return <HTMLElementRenderer node={templateNode} context={context} nodeKey={nodeKey} />;
    case 'special':
      return <SpecialComponentRenderer node={templateNode} context={context} nodeKey={nodeKey} />;
    default:
      // Try rendering as HTML element anyway (for custom elements)
      if (type && typeof type === 'string') {
        console.warn(`Unknown element type "${type}", attempting to render as HTML element`);
        return <HTMLElementRenderer node={templateNode} context={context} nodeKey={nodeKey} />;
      }
      console.warn(`Cannot render node with type: ${type}`);
      return null;
  }
}

export default NodeRenderer;

